import { useSetMerchantTradeDiscountMutation } from "@/Apollo/hooks/merchant/mutations/useSetMerchantTradeDiscountMutation";
import { useMeTradeDiscountQuery } from "@/Apollo/hooks/merchant/queries/useMeTradeDiscountQuery";
import { MerchantType } from "@/Apollo/schema";
import {
  SaveTradeDiscountResult,
  tradeDiscountErrorCode,
} from "@/features/pro-buyer-discount/utils/trade-discount.utils";
import useMerchant from "@/hooks/useMerchant";

/**
 * Every query that renders a gateway-computed Pro Buyer price: the ads list
 * marks and the own-PDP line re-read after a save.
 */
const TRADE_PRICE_QUERIES = ["MyProducts", "ProductBySlug"];

/**
 * WHO-5654. The seller's own Pro Buyer discount, read through Apollo.
 *
 * `available` is the single gate for every seller-side surface: true only
 * for a business account whose gateway payload carries `tradeDiscount`,
 * which the gateway returns null while the `pro_buyer_program` flag is off.
 * So "flag off = nothing renders anywhere" needs no second flag on the web.
 *
 * `cache-and-network`: a change CS makes in the CMS shows on the next
 * navigation without a hard reload. No optimistic updates: the block, the
 * ads marks and the PDP line change only after the gateway confirmed.
 */
export const useTradeDiscount = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { id: merchantId, merchant } = useMerchant();
  const isBusiness = merchant?.type === MerchantType.Business;

  const { data, loading } = useMeTradeDiscountQuery({
    skip: !enabled || !merchantId || !isBusiness,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const [mutate, { loading: saving }] = useSetMerchantTradeDiscountMutation({
    refetchQueries: TRADE_PRICE_QUERIES,
    awaitRefetchQueries: true,
  });

  const discount =
    data?.me?.merchants.find(candidate => candidate.id === merchantId)
      ?.tradeDiscount ?? null;

  const available = isBusiness && discount !== null;

  const save = async (input: {
    enabled: boolean;
    pct: number | null;
  }): Promise<SaveTradeDiscountResult> => {
    try {
      const result = await mutate({ variables: input });
      const saved = result.data?.setMerchantTradeDiscount.tradeDiscount;

      if (!saved) {
        return { ok: false, code: "FEATURE_DISABLED" };
      }

      return { ok: true, discount: saved };
    } catch (error) {
      return { ok: false, code: tradeDiscountErrorCode(error) };
    }
  };

  return {
    /** Business account and the program is on for this viewer. */
    available,
    loading: isBusiness && loading && !data,
    discount: available ? discount : null,
    /** The percentage the seller offers right now, or null when off. */
    activePct: available && discount?.enabled ? discount.pct ?? null : null,
    saving,
    merchantId: merchantId ?? null,
    save,
  };
};
